import { useEffect, useState } from "react";
import { TrendingUp, Shield, Clock, Star, Users, MapPin, ArrowRight } from "lucide-react";
import EnrichmentModal from "@/pages/manchester/EnrichmentModal";

const values = [
  { icon: TrendingUp, title: "Performance First", desc: "We're obsessed with maximising your rental income. Dynamic pricing, multi-platform distribution and a direct booking engine that cuts OTA fees." },
  { icon: Shield, title: "Trust & Transparency", desc: "No hidden fees, no lock-in contracts. Clear monthly reporting. You always know exactly what you're earning and why." },
  { icon: Clock, title: "Speed to Market", desc: "From your first call to being live on Airbnb in 7 days. Professional photography, compliance, listings and pricing - all handled." },
  { icon: Users, title: "Guest-Led Excellence", desc: "Our 24/7 guest experience team ensures every stay is a 5-star one. Happy guests leave great reviews. Great reviews drive more bookings." },
  { icon: Star, title: "Owner Partnership", desc: "You keep full control of your calendar. We operate as a true partner - flexible, responsive, and always working in your best interests." },
  { icon: MapPin, title: "Local Expertise", desc: "We know the UK short-let market inside out. From Manchester to London to Bath, we combine local insight with national infrastructure." },
];

const stats = [
  { val: "35,000+", label: "Stays managed" },
  { val: "5.0", label: "Google rating (90+ reviews)" },
  { val: "16+", label: "UK locations" },
  { val: "4.76", label: "Airbnb rating (1,000+ reviews)" },
];

export default function About() {
  const [showModal, setShowModal] = useState(false);
  const [activeIndex, setActiveIndex] = useState(1);

  useEffect(() => {
    document.title = "About Truestays | UK Airbnb Management Company";
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.name = name; document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("description", "Truestays has managed 35,000+ stays across the UK since 2019. Airbnb Superhosts, 5.0 on Google (90+ reviews) and 4.76 on Airbnb (1,000+ reviews), operating across Manchester, Liverpool, Stoke-on-Trent and 17+ more cities.");
    setMeta("robots", "index, follow");
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.appendChild(canonical); }
    canonical.href = window.location.origin + "/about";
  }, []);

  return (
    <div style={{ background: "#fff", minHeight: "100vh" }}>

      {/* Hero - matching Interiors style */}
      <section
        style={{
          position: "relative",
          paddingTop: "clamp(140px, 16vw, 180px)",
          paddingBottom: "clamp(48px, 6vw, 80px)",
          paddingLeft: "clamp(20px, 5vw, 80px)",
          paddingRight: "clamp(20px, 5vw, 80px)",
          backgroundColor: "#f4f2f0",
          overflow: "hidden",
        }}
      >
        <div style={{ maxWidth: 1200, margin: "0 auto", position: "relative", zIndex: 2, textAlign: "center" }}>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "#888", marginBottom: 16, marginTop: 0 }}>
            About Truestays
          </p>
          <h1 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "clamp(36px, 5.5vw, 64px)", lineHeight: 1.05, letterSpacing: "-0.03em", color: "#1A1A1A", margin: "0 auto 20px", maxWidth: 820 }}>
            Airbnb Management.<br />
            <span style={{ color: "#1A1A1A" }}>Done Properly.</span>
          </h1>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "clamp(16px, 2vw, 19px)", fontWeight: 400, color: "#555", lineHeight: 1.65, maxWidth: 560, margin: "0 auto 40px" }}>
            We're a team of property and hospitality specialists on a mission to make short-let management effortless and profitable for UK landlords.
          </p>

          <div style={{ display: "flex", justifyContent: "center", gap: 12, flexWrap: "wrap" }} className="about-hero-btns">
            <button
              onClick={() => setShowModal(true)}
              style={{ background: "hsl(var(--primary))", color: "#fff", border: "none", borderRadius: 10, padding: "15px 28px", fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, transition: "background 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.background = "#C4C024"}
              onMouseLeave={e => e.currentTarget.style.background = "hsl(var(--primary))"}
            >
              Get Free Valuation <ArrowRight size={16} />
            </button>
            <a
              href="#our-story"
              style={{ background: "transparent", border: "1.5px solid #D5D2CE", color: "#1A1A1A", borderRadius: 10, padding: "14px 26px", fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 500, textDecoration: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", transition: "border-color 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.borderColor = "#1A1A1A"}
              onMouseLeave={e => e.currentTarget.style.borderColor = "#D5D2CE"}
            >
              Our story
            </a>
          </div>
        </div>

        {/* Hero image strip */}
        <div style={{ maxWidth: 1200, margin: "40px auto 0", position: "relative", zIndex: 2 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }} className="about-hero-grid">
            <div style={{ borderRadius: 14, overflow: "hidden", height: 220 }}>
              <img src="https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/8792b7eef_6.jpg" alt="Modern property interior" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            </div>
            <div className="about-hero-img-2" style={{ borderRadius: 14, overflow: "hidden", height: 220 }}>
              <img src="https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/c726a3d38_7.jpg" alt="Styled living space" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            </div>
            <div className="about-hero-img-3" style={{ borderRadius: 14, overflow: "hidden", height: 220 }}>
              <img src="https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/ff70273fd_8.jpg" alt="Contemporary kitchen" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            </div>
          </div>
        </div>
      </section>

      {/* Stats section */}
      <section style={{ backgroundColor: "#F7F7F5", padding: "clamp(48px, 6vw, 64px) clamp(20px, 5vw, 80px)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 24 }} className="about-stats-grid">
            {stats.map(({ val, label }) => (
              <div key={label} style={{ textAlign: "center" }}>
                <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: "clamp(28px, 3vw, 36px)", color: "#1A1A1A", margin: 0, lineHeight: 1.1 }}>{val}</p>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#888", margin: "8px 0 0" }}>{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story section - white background */}
      <section id="our-story" style={{ backgroundColor: "#fff", padding: "clamp(60px, 7vw, 96px) clamp(20px, 5vw, 80px)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80, alignItems: "center" }} className="about-story">
            <div>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "#4A6DC7", marginBottom: 12, marginTop: 0 }}>Who We Are</p>
              <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "clamp(32px, 4vw, 44px)", color: "#1A1A1A", margin: "0 0 20px", letterSpacing: "-0.02em", lineHeight: 1.15 }}>
                Built for landlords who want results, not excuses.
              </h2>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: "#666", lineHeight: 1.75, marginBottom: 16, marginTop: 0 }}>
                Truestays was founded by property owners who were frustrated with opaque management companies, poor communication, and disappointing returns. We set out to build the service we always wanted as landlords ourselves.
              </p>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: "#666", lineHeight: 1.75, marginBottom: 24, marginTop: 0 }}>
                Today we manage properties across 16+ UK cities, combining cutting-edge technology with a genuinely hands-on team. Our direct booking engine generates around 31% of all bookings - significantly reducing OTA commission and putting more money in your pocket.
              </p>
              <a href="/support" style={{ background: "hsl(var(--primary))", color: "#fff", borderRadius: 8, padding: "14px 28px", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600, textDecoration: "none", display: "inline-block", transition: "background 0.2s" }}
                onMouseEnter={e => e.target.style.background = "#C4C024"}
                onMouseLeave={e => e.target.style.background = "hsl(var(--primary))"}>
                Get in touch
              </a>
            </div>
            <div style={{ borderRadius: 20, overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.12)" }}>
              <img
                src="https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/f25de0a34_11.jpg"
                alt="Truestays property interior"
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values - matching Interiors WhyInteriors style */}
      <section style={{ backgroundColor: "#fff", padding: "clamp(60px, 7vw, 96px) clamp(20px, 5vw, 80px)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "#888", marginBottom: 12, marginTop: 0 }}>Our Values</p>
            <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "clamp(28px, 4vw, 44px)", letterSpacing: "-0.02em", lineHeight: 1.15, color: "#1A1A1A", margin: "0 auto 14px", maxWidth: 640 }}>
              What drives us
            </h2>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: "#666", lineHeight: 1.6, maxWidth: 520, margin: "0 auto" }}>
              We're guided by principles that put property owners first - transparency, performance, and partnership.
            </p>
          </div>

          <div className="about-values-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 }}>
            {values.map(({ icon: Icon, title, desc }, i) => {
              const isActive = activeIndex === i;
              return (
                <div
                  key={title}
                  onMouseEnter={() => setActiveIndex(i)}
                  style={{
                    background: isActive ? "#1A1A1A" : "#F7F7F5",
                    borderRadius: 14,
                    padding: "26px 26px 30px",
                    display: "flex",
                    flexDirection: "column",
                    transform: isActive ? "rotate(-1.5deg) scale(1.03)" : "rotate(0deg) scale(1)",
                    boxShadow: isActive ? "0 20px 50px rgba(0,0,0,0.22)" : "0 0 0 rgba(0,0,0,0)",
                    transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
                    zIndex: isActive ? 2 : 1,
                    position: "relative",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 44 }}>
                    <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: 16, color: isActive ? "#fff" : "#1A1A1A", letterSpacing: "-0.01em", transition: "color 0.25s" }}>
                      {title}
                    </span>
                    <div style={{ width: 34, height: 34, borderRadius: 9, background: isActive ? "rgba(255,255,255,0.15)" : "#ECEAE6", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "background 0.25s" }}>
                      <Icon size={16} color={isActive ? "#fff" : "#555"} strokeWidth={1.8} />
                    </div>
                  </div>
                  <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13.5, color: isActive ? "rgba(255,255,255,0.75)" : "#666", lineHeight: 1.65, margin: 0, transition: "color 0.25s" }}>
                    {desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>



      {showModal && (
        <EnrichmentModal
          mode="full"
          baseForm={{}}
          onClose={() => setShowModal(false)}
          onSubmitted={() => setShowModal(false)}
        />
      )}

      <style>{`
        @media (max-width: 900px) {
          .about-story { grid-template-columns: 1fr !important; gap: 48px !important; }
          .about-values-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .about-stats-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 32px !important; }
        }
        @media (max-width: 768px) {
          .about-hero-btns { flex-direction: column !important; align-items: stretch !important; width: 100%; max-width: 400px; margin: 0 auto; }
          .about-hero-btns button, .about-hero-btns a { width: 100% !important; box-sizing: border-box !important; }
          .about-hero-img-2, .about-hero-img-3 { display: none !important; }
          .about-hero-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 560px) {
          .about-values-grid { grid-template-columns: 1fr !important; }
          .about-stats-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}